"use client"

import { useEffect, useMemo } from "react"
import type { EmailIntegration, Sequence } from "@/schema"
import { NULL_SENTINEL_VALUE } from "@ui/forms/inputs/select-input"
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"
import { Badge } from "@ui/shad/badge"
import { ControllerRenderProps, FieldValues, Path, useFormContext } from "react-hook-form"

import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { UserAvatar } from "@/components/users/user-avatar"

type EmailIntegrationOptionSource = Pick<
  EmailIntegration,
  "id" | "connected_email" | "is_primary" | "sending_addresses"
> & {
  user?: EmailIntegration["user"]
}

interface EmailIntegrationOptionDisplayData {
  value: string
  emailLabel: string
  ownerId?: string
  ownerName: string
  isYou: boolean
  badges: string[]
}

interface EmailIntegrationSelectFieldBaseProps {
  sequence: Sequence
  label?: string
  autoSelect?: boolean
  allowEmpty?: boolean
  emptyOptionLabel?: string
}

type EmailIntegrationSelectFieldProps<T extends FieldValues> = EmailIntegrationSelectFieldBaseProps &
  (
    | {
        field: ControllerRenderProps<T, Path<T>>
        value?: never
        onChange?: never
      }
    | {
        field?: never
        value: string | null | undefined
        onChange: (value: string | null) => void
      }
  )

function formatEmailIntegrationPrimaryLabel(integration: EmailIntegrationOptionSource) {
  const defaultAddress = integration.sending_addresses?.find((a) => a.is_default)
  if (defaultAddress) {
    return defaultAddress.display_name
      ? `${defaultAddress.display_name} <${defaultAddress.email}>`
      : defaultAddress.email
  }
  return integration.connected_email || "Email integration"
}

function EmailIntegrationOptionDisplay({
  option,
  showBadges = true,
}: {
  option: EmailIntegrationOptionDisplayData
  showBadges?: boolean
}) {
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="text-foreground text-sm leading-none font-medium">{option.emailLabel}</span>
      <div className="text-muted-foreground flex items-center gap-2 text-xs leading-none">
        {option.ownerId ? (
          <UserAvatar userId={option.ownerId} size="xs" />
        ) : (
          <div className="bg-muted text-muted-foreground flex size-5 items-center justify-center rounded-full text-[10px] uppercase">
            {option.ownerName.charAt(0)}
          </div>
        )}
        <span className="truncate">
          {option.ownerName}
          {option.isYou ? " (You)" : ""}
        </span>
        {showBadges &&
          option.badges.map((badge) => (
            <Badge key={badge} variant="secondary" size="xs">
              {badge}
            </Badge>
          ))}
      </div>
    </div>
  )
}

export function EmailIntegrationSelectField<T extends FieldValues>({
  sequence,
  field,
  value,
  onChange,
  label = "Send emails from",
  autoSelect = true,
  allowEmpty = false,
  emptyOptionLabel = "Use sequence default",
}: EmailIntegrationSelectFieldProps<T>) {
  const formContext = useFormContext<T>()
  const { data: currentUser, isFetching: isFetchingCurrentUser, refetch: refetchCurrentUser } = useFetchCurrentUser()

  useEffect(() => {
    refetchCurrentUser()
  }, [refetchCurrentUser])

  const currentUserId = currentUser?.id
  const currentValue = (field ? field.value : value) as string | null | undefined

  const setValue = (nextValue: string | null) => {
    if (field) {
      field.onChange(nextValue)
      return
    }
    onChange?.(nextValue)
  }

  const accessibleEmailIntegrations = useMemo(
    () => currentUser?.accessible_email_integrations ?? [],
    [currentUser?.accessible_email_integrations]
  )

  const integrationOptions = useMemo<EmailIntegrationOptionDisplayData[]>(() => {
    const buildOption = (
      integration: EmailIntegrationOptionSource,
      forceSequenceDefault = false
    ): EmailIntegrationOptionDisplayData => {
      const ownerId = integration.user?.id
      const ownerName = integration.user?.full_name || integration.user?.email || "Unknown user"
      const isYou = !!ownerId && ownerId === currentUserId
      const isSequenceDefault = forceSequenceDefault || integration.id === sequence.default_email_integration_id
      const badges = isSequenceDefault ? ["Sequence default"] : []

      return {
        value: integration.id,
        emailLabel: formatEmailIntegrationPrimaryLabel(integration),
        ownerId,
        ownerName,
        isYou,
        badges,
      }
    }

    const baseOptions = accessibleEmailIntegrations.map((integration) => buildOption(integration))
    const defaultIntegration = sequence.default_email_integration as EmailIntegrationOptionSource | undefined

    if (!defaultIntegration) {
      return baseOptions
    }

    const defaultId = defaultIntegration.id
    const existingIndex = baseOptions.findIndex((option) => option.value === defaultId)
    const defaultOption = buildOption(defaultIntegration, true)

    if (existingIndex >= 0) {
      return baseOptions.map((option, index) => (index === existingIndex ? defaultOption : option))
    }

    return [defaultOption, ...baseOptions]
  }, [
    accessibleEmailIntegrations,
    currentUserId,
    sequence.default_email_integration,
    sequence.default_email_integration_id,
  ])

  const fallbackEmailIntegrationId = useMemo(() => {
    if (sequence.default_email_integration_id) {
      return sequence.default_email_integration_id
    }

    if (!currentUserId) {
      return accessibleEmailIntegrations[0]?.id ?? null
    }

    const ownedIntegrations = accessibleEmailIntegrations.filter((integration) => integration.user.id === currentUserId)
    const primaryIntegration = ownedIntegrations.find((integration) => integration.is_primary)
    const fallbackIntegration = primaryIntegration || ownedIntegrations[0] || accessibleEmailIntegrations[0]

    return fallbackIntegration?.id ?? null
  }, [accessibleEmailIntegrations, currentUserId, sequence.default_email_integration_id])

  useEffect(() => {
    if (!autoSelect) return
    if (!fallbackEmailIntegrationId) return
    if (currentValue) return

    if (field && formContext) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formContext.setValue(field.name as Path<T>, fallbackEmailIntegrationId as any, {
        shouldDirty: false,
        shouldTouch: false,
      })
    } else {
      setValue(fallbackEmailIntegrationId)
    }
    // setValue is stable enough for this sync; including it would re-run on every render when using onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelect, fallbackEmailIntegrationId, currentValue, field, formContext])

  const isLoadingOptions = isFetchingCurrentUser && integrationOptions.length === 0
  const selectedOption = currentValue ? integrationOptions.find((option) => option.value === currentValue) : null
  const hasOptions = integrationOptions.length > 0
  const selectValue = currentValue || (allowEmpty ? NULL_SENTINEL_VALUE : undefined)
  const showingEmptyOption = allowEmpty && !currentValue

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium" htmlFor="email_integration_id">
        {label}
      </Label>
      <Select
        value={selectValue}
        onValueChange={(nextValue) => {
          if (allowEmpty && nextValue === NULL_SENTINEL_VALUE) {
            setValue(null)
            return
          }
          setValue(nextValue)
        }}
        disabled={!hasOptions && !isLoadingOptions && !allowEmpty}
      >
        <SelectTrigger id="email_integration_id" className="h-fit w-full py-2">
          {selectedOption ? (
            <SelectValue asChild>
              <EmailIntegrationOptionDisplay option={selectedOption} />
            </SelectValue>
          ) : showingEmptyOption ? (
            <SelectValue asChild>
              <span className="text-muted-foreground text-sm">{emptyOptionLabel}</span>
            </SelectValue>
          ) : (
            <SelectValue
              placeholder={
                hasOptions
                  ? "Select an email integration"
                  : isLoadingOptions
                    ? "Loading email integrations..."
                    : "No email integrations available"
              }
            />
          )}
        </SelectTrigger>
        <SelectContent>
          {allowEmpty && (
            <SelectItem value={NULL_SENTINEL_VALUE} textValue={emptyOptionLabel} className="py-2">
              <span className="text-muted-foreground text-sm">{emptyOptionLabel}</span>
            </SelectItem>
          )}
          {integrationOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              textValue={option.emailLabel}
              className="space-y-1 py-2"
            >
              <EmailIntegrationOptionDisplay option={option} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!hasOptions && !isLoadingOptions && !allowEmpty && (
        <p className="text-muted-foreground text-xs">
          Add or request access to an email integration to send emails from this sequence.
        </p>
      )}
    </div>
  )
}
